import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  UserGroupIcon,
  BuildingOfficeIcon,
  UserIcon,
  BriefcaseIcon,
  CalendarIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline'
import { ArrowTrendingUpIcon, ArrowRightIcon } from '@heroicons/react/20/solid'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts'
import { createCrudApi } from '../../lib/api'
import { useTranslation } from '../../hooks/useTranslation'
import { useCartridgeDashboardWidgets } from '../../hooks/useCartridgeUI'
import { CartridgeWidgetsGrid } from '../../components/cartridge/CartridgeWidgetRenderer'
import type { Lead, Account, Contact, Opportunity, Event } from '../../../db/schema'

const leadsApi = createCrudApi<Lead, unknown>('leads')
const accountsApi = createCrudApi<Account, unknown>('accounts')
const contactsApi = createCrudApi<Contact, unknown>('contacts')
const opportunitiesApi = createCrudApi<Opportunity, unknown>('opportunities')
const eventsApi = createCrudApi<Event, unknown>('events')

const STAGE_COLORS: Record<string, string> = {
  qualification: '#10b981',
  proposal: '#3b82f6',
  negotiation: '#f59e0b',
  closed_won: '#22c55e',
  closed_lost: '#ef4444',
}

const LEAD_STATUS_COLORS: Record<string, string> = {
  new: '#3b82f6',
  contacted: '#f59e0b',
  qualified: '#22c55e',
  unqualified: '#6b7280',
}

export function DashboardPage() {
  const { t } = useTranslation()
  const { widgets: cartridgeWidgets } = useCartridgeDashboardWidgets()

  const { data: leads } = useQuery({
    queryKey: ['leads'],
    queryFn: () => leadsApi.list(),
  })

  const { data: accounts } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => accountsApi.list(),
  })

  const { data: contacts } = useQuery({
    queryKey: ['contacts'],
    queryFn: () => contactsApi.list(),
  })

  const { data: opportunities } = useQuery({
    queryKey: ['opportunities'],
    queryFn: () => opportunitiesApi.list(),
  })

  const { data: events } = useQuery({
    queryKey: ['events'],
    queryFn: () => eventsApi.list(),
  })

  // Calculate stats
  const leadsData = leads?.data || []
  const accountsData = accounts?.data || []
  const contactsData = contacts?.data || []
  const opportunitiesData = opportunities?.data || []
  const eventsData = events?.data || []

  const totalLeads = leadsData.length
  const totalAccounts = accountsData.length
  const totalContacts = contactsData.length
  const totalOpportunities = opportunitiesData.length
  const scheduledEvents = eventsData.filter(e => e.status === 'scheduled').length

  // Calculate pipeline value
  const pipelineValue = opportunitiesData.reduce((sum, opp) => {
    if (opp.stage !== 'closed_lost' && opp.stage !== 'closed_won') {
      return sum + (parseFloat(opp.amount as unknown as string) || 0)
    }
    return sum
  }, 0)

  const wonValue = opportunitiesData.reduce((sum, opp) => {
    if (opp.stage === 'closed_won') {
      return sum + (parseFloat(opp.amount as unknown as string) || 0)
    }
    return sum
  }, 0)

  // Prepare chart data - Opportunities by Stage
  const stageData = opportunitiesData.reduce((acc, opp) => {
    const existing = acc.find(s => s.stage === opp.stage)
    if (existing) {
      existing.count += 1
      existing.value += parseFloat(opp.amount as unknown as string) || 0
    } else {
      acc.push({
        stage: opp.stage,
        count: 1,
        value: parseFloat(opp.amount as unknown as string) || 0,
      })
    }
    return acc
  }, [] as { stage: string; count: number; value: number }[])

  // Prepare chart data - Leads by Status
  const leadStatusData = leadsData.reduce((acc, lead) => {
    const existing = acc.find(s => s.status === lead.status)
    if (existing) {
      existing.count += 1
    } else {
      acc.push({
        status: lead.status,
        count: 1,
      })
    }
    return acc
  }, [] as { status: string; count: number }[])

  const stats = [
    {
      name: t('pages.dashboard.stats.leads'),
      value: totalLeads,
      icon: UserGroupIcon,
      href: '/leads',
      color: 'bg-blue-500',
    },
    {
      name: t('pages.dashboard.stats.accounts'),
      value: totalAccounts,
      icon: BuildingOfficeIcon,
      href: '/accounts',
      color: 'bg-purple-500',
    },
    {
      name: t('pages.dashboard.stats.contacts'),
      value: totalContacts,
      icon: UserIcon,
      href: '/contacts',
      color: 'bg-indigo-500',
    },
    {
      name: t('pages.dashboard.stats.opportunities'),
      value: totalOpportunities,
      icon: BriefcaseIcon,
      href: '/opportunities',
      color: 'bg-brand-500',
    },
    {
      name: t('pages.dashboard.stats.scheduledEvents'),
      value: scheduledEvents,
      icon: CalendarIcon,
      href: '/events',
      color: 'bg-amber-500',
    },
  ]

  return (
    <>
      {/* Secondary column - Quick Stats */}
      <aside className="fixed inset-y-4 left-72 hidden w-[calc((100vw-18rem-2rem)/3)] overflow-y-auto bg-white px-4 py-6 sm:px-6 lg:px-8 xl:block rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)]">
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{t('pages.dashboard.title')}</h2>
            <p className="mt-1 text-sm text-gray-500">
              {t('pages.dashboard.description')}
            </p>
          </div>

          {/* Pipeline Value Card */}
          <div className="bg-gradient-to-br from-brand-500 to-brand-600 p-4 rounded-lg shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <CurrencyDollarIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-white/80">{t('pages.dashboard.pipelineValue')}</p>
                <p className="text-2xl font-bold text-white">
                  ${pipelineValue.toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          {/* Won Value Card */}
          <div className="bg-gradient-to-br from-green-500 to-green-600 p-4 rounded-lg shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <ArrowTrendingUpIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-white/80">{t('pages.dashboard.wonValue')}</p>
                <p className="text-2xl font-bold text-white">
                  ${wonValue.toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          {/* Leads by Status Pie Chart */}
          {leadStatusData.length > 0 && (
            <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
              <h3 className="text-sm font-medium text-gray-900 mb-3">{t('pages.dashboard.leadsByStatus')}</h3>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={leadStatusData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={70}
                      paddingAngle={2}
                      dataKey="count"
                      nameKey="status"
                    >
                      {leadStatusData.map((entry) => (
                        <Cell
                          key={entry.status}
                          fill={LEAD_STATUS_COLORS[entry.status] || '#6b7280'}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value, name) => [value ?? 0, t(`status.${name}` as never)]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {leadStatusData.map((entry) => (
                  <div key={entry.status} className="flex items-center gap-1.5 text-xs">
                    <div
                      className="w-2.5 h-2.5 rounded-full"
                      style={{ backgroundColor: LEAD_STATUS_COLORS[entry.status] || '#6b7280' }}
                    />
                    <span className="text-gray-600">{t(`status.${entry.status}` as never)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </aside>

      {/* Main content */}
      <div className="xl:pl-[calc((100vw-18rem-2rem)/3)] xl:ml-4 p-4 xl:p-0 xl:pr-4 xl:py-4">
        <div className="bg-white rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)] min-h-[calc(100vh-2rem)] px-4 py-10 sm:px-6 lg:px-8 lg:py-6">
          {/* Mobile header */}
          <div className="xl:hidden mb-6">
            <h1 className="text-2xl font-semibold text-gray-900">{t('pages.dashboard.title')}</h1>
            <p className="mt-1 text-sm text-gray-500">{t('pages.dashboard.description')}</p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-8">
            {stats.map((stat) => (
              <Link
                key={stat.name}
                to={stat.href}
                className="relative group bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5 hover:ring-brand-500/50 hover:shadow-md transition-all"
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${stat.color}`}>
                    <stat.icon className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{stat.name}</p>
                    <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
                  </div>
                </div>
                <ArrowRightIcon className="absolute top-4 right-4 h-4 w-4 text-gray-400 group-hover:text-brand-500 transition-colors" />
              </Link>
            ))}
          </div>

          {/* Mobile Pipeline/Won Cards */}
          <div className="xl:hidden grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
            <div className="bg-gradient-to-br from-brand-500 to-brand-600 p-4 rounded-lg shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <CurrencyDollarIcon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <p className="text-sm text-white/80">{t('pages.dashboard.pipelineValue')}</p>
                  <p className="text-2xl font-bold text-white">
                    ${pipelineValue.toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-green-500 to-green-600 p-4 rounded-lg shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <ArrowTrendingUpIcon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <p className="text-sm text-white/80">{t('pages.dashboard.wonValue')}</p>
                  <p className="text-2xl font-bold text-white">
                    ${wonValue.toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Opportunities by Stage Chart */}
          {stageData.length > 0 && (
            <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5 mb-8">
              <h3 className="text-sm font-medium text-gray-900 mb-4">{t('pages.dashboard.opportunitiesByStage')}</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={stageData} layout="vertical">
                    <XAxis type="number" />
                    <YAxis
                      type="category"
                      dataKey="stage"
                      width={100}
                      tickFormatter={(value) => t(`status.${value}` as never)}
                    />
                    <Tooltip
                      formatter={(value, name) => {
                        const numValue = Number(value) || 0
                        if (name === 'value') return [`$${numValue.toLocaleString()}`, t('pages.dashboard.value')]
                        return [numValue, t('pages.dashboard.count')]
                      }}
                      labelFormatter={(label) => t(`status.${label}` as never)}
                    />
                    <Bar dataKey="count" name="count" radius={[0, 4, 4, 0]}>
                      {stageData.map((entry) => (
                        <Cell
                          key={entry.stage}
                          fill={STAGE_COLORS[entry.stage] || '#6b7280'}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="flex flex-wrap gap-3 mt-4">
                {stageData.map((entry) => (
                  <div key={entry.stage} className="flex items-center gap-1.5 text-xs">
                    <div
                      className="w-2.5 h-2.5 rounded-full"
                      style={{ backgroundColor: STAGE_COLORS[entry.stage] || '#6b7280' }}
                    />
                    <span className="text-gray-600">{t(`status.${entry.stage}` as never)}</span>
                    <span className="text-gray-400">({entry.count})</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-4">{t('pages.dashboard.quickActions')}</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Link
                to="/leads"
                className="flex items-center gap-2 p-3 rounded-lg bg-gray-50 hover:bg-brand-50 text-gray-700 hover:text-brand-700 transition-colors text-sm font-medium"
              >
                <UserGroupIcon className="h-5 w-5" />
                {t('pages.dashboard.actions.newLead')}
              </Link>
              <Link
                to="/accounts"
                className="flex items-center gap-2 p-3 rounded-lg bg-gray-50 hover:bg-brand-50 text-gray-700 hover:text-brand-700 transition-colors text-sm font-medium"
              >
                <BuildingOfficeIcon className="h-5 w-5" />
                {t('pages.dashboard.actions.newAccount')}
              </Link>
              <Link
                to="/opportunities"
                className="flex items-center gap-2 p-3 rounded-lg bg-gray-50 hover:bg-brand-50 text-gray-700 hover:text-brand-700 transition-colors text-sm font-medium"
              >
                <BriefcaseIcon className="h-5 w-5" />
                {t('pages.dashboard.actions.newOpportunity')}
              </Link>
              <Link
                to="/events"
                className="flex items-center gap-2 p-3 rounded-lg bg-gray-50 hover:bg-brand-50 text-gray-700 hover:text-brand-700 transition-colors text-sm font-medium"
              >
                <CalendarIcon className="h-5 w-5" />
                {t('pages.dashboard.actions.newEvent')}
              </Link>
            </div>
          </div>

          {/* Cartridge Widgets */}
          <CartridgeWidgetsGrid widgets={cartridgeWidgets} />
        </div>
      </div>
    </>
  )
}
